import { CustomNavBar, NavBarTab } from 'components/facets/CustomNavBar/CustomNavBar'
import { getUserPickingLists } from 'lib/api'
import { useOrderStore } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getOrderState, ORDER_SIDEBAR_TABS } from 'shared'

export const OrdersSidebar = React.memo(function OrdersSidebar(): JSX.Element {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const {
    activeTab,
    orders,
    activeOrdersFilters,
    setActiveTab,
    setActivePickingList,
    setExpandedOrder,
    setActiveOrdersFilters,
    setUserPickingLists,
    setLoadingPickingLists,
    setSelectedOrderIds,
    setSelectedOrders,
  } = useOrderStore()

  const [numOrdersFulfilment, setNumOrdersFulfilment] = useState(0)
  const [numOrdersCancelled, setNumOrdersCancelled] = useState(0)
  const [numOrdersManagePickingLists, setNumOrdersManagePickingLists] = useState(0)
  const [numOrdersPickArticles, setNumOrdersPickArticles] = useState(0)
  const [numOrdersPackage, setNumOrdersPackage] = useState(0)

  useEffect(() => {
    let countOrdersFulfilment = 0
    let countOrdersCancelled = 0
    let countOrdersManagePickingLists = 0
    let countOrdersPickArticles = 0
    let countOrdersPackage = 0

    orders.forEach((o) => {
      const orderState = getOrderState(o)
      if (!['completed', 'evaluated', 'canceled'].includes(orderState)) {
        countOrdersFulfilment++
      }
      if (['canceled'].includes(orderState)) {
        countOrdersCancelled++
      }
      if (['paid'].includes(orderState)) {
        countOrdersManagePickingLists++
      }
      if (['picking'].includes(orderState)) {
        countOrdersPickArticles++
      }
      if (['picked', 'packed', 'sent'].includes(orderState)) {
        countOrdersPackage++
      }
    })

    setNumOrdersFulfilment(countOrdersFulfilment)
    setNumOrdersCancelled(countOrdersCancelled)
    setNumOrdersManagePickingLists(countOrdersManagePickingLists)
    setNumOrdersPickArticles(countOrdersPickArticles)
    setNumOrdersPackage(countOrdersPackage)
  }, [orders])

  useEffect(() => {
    setExpandedOrder(undefined)
    clearIssueFilter()
  }, [activeTab])

  const clearIssueFilter = useCallback(() => {
    if (activeOrdersFilters.includes('issues')) {
      const newActiveOrdersFilters = [...activeOrdersFilters]
      const idx = newActiveOrdersFilters.indexOf('issues')
      newActiveOrdersFilters.splice(idx, 1)
      setActiveOrdersFilters(newActiveOrdersFilters)
    }
  }, [activeOrdersFilters, setActiveOrdersFilters])

  const loadUserPickingLists = useCallback(async () => {
    setLoadingPickingLists(true)
    try {
      const newUserPickingLists = await getUserPickingLists('open')
      setUserPickingLists(newUserPickingLists)
    } catch (error) {
      console.error('getUserPickingLists failed:', error)
      enqueueSnackbar(t('orders.picking.errorLoadingPickingLists'), { variant: 'error' })
    } finally {
      setLoadingPickingLists(false)
    }
  }, [setUserPickingLists])

  const onClick = useCallback(
    (key: string) => {
      const tab = Number(key)
      setActivePickingList(undefined)
      if (tab === ORDER_SIDEBAR_TABS.PICK_ARTICLES) {
        loadUserPickingLists()
      }
      setActiveTab(tab)
      setSelectedOrderIds([])
      setSelectedOrders([])
    },
    [activeTab]
  )

  const tabLabel = (
    title: string,
    count: number,
    subtitle: string,
    secondCount?: number,
    secondLabel?: string
  ) => (
    <div>
      <h6 className="m-0 p-0">
        <strong>{title}</strong>
      </h6>
      <span className="small m-0 p-0">
        {count} {subtitle}
      </span>
      {secondLabel != null && (
        <span className="small m-0 p-0">
          {' '}
          ({secondCount} {secondLabel})
        </span>
      )}
    </div>
  )

  const tabs: NavBarTab[] = [
    {
      key: String(ORDER_SIDEBAR_TABS.FULFILMENT_CENTER),
      dataTestid: 'fulfilment-center',
      label: tabLabel(
        'Fulfilment Center',
        numOrdersFulfilment,
        'orders',
        numOrdersCancelled,
        'cancelled'
      ),
    },
    {
      key: String(ORDER_SIDEBAR_TABS.MANAGE_PICKING_LISTS),
      dataTestid: 'manage-pickinglist',
      label: tabLabel('1. Manage Picking Lists', numOrdersManagePickingLists, 'new orders'),
    },
    {
      key: String(ORDER_SIDEBAR_TABS.PICK_ARTICLES),
      dataTestid: 'pick-articles',
      label: tabLabel('2. Pick Articles', numOrdersPickArticles, 'picking orders'),
    },
    {
      key: String(ORDER_SIDEBAR_TABS.PACKAGE),
      dataTestid: 'package-label-ship',
      label: tabLabel('3. Package, Label, Ship', numOrdersPackage, 'orders'),
    },
  ]

  return <CustomNavBar tabs={tabs} activeTab={String(activeTab)} onTabChange={onClick} />
})
